import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { X, CheckCircle2, AlertCircle, ArrowRight, ShieldCheck, Database, FileSpreadsheet } from 'lucide-react';

interface CatalogAuditLeadModalProps {
  isOpen: boolean;
  onClose: () => void;
  originTab?: 'quick' | 'reforma' | 'audit';
}

const ERP_OPTIONS = [
  'Senior Sistemas (Mega / RPX)',
  'Totvs Protheus',
  'Totvs Datasul / RM',
  'SAP Business One / ECC',
  'Omie',
  'Bling',
  'Tiny ERP',
  'Linx',
  'Sankhya',
  'Conta Azul',
  'Planilhas Excel / Sem ERP',
  'Outro Sistema',
];

const SKU_OPTIONS = [
  { id: 'ate_500', label: 'Até 500 itens' },
  { id: '500_a_5000', label: '500 a 5.000 itens' },
  { id: 'mais_5000', label: 'Mais de 5.000 itens' },
];

export const CatalogAuditLeadModal: React.FC<CatalogAuditLeadModalProps> = ({
  isOpen,
  onClose,
  originTab = 'audit',
}) => {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [erpSystem, setErpSystem] = useState(ERP_OPTIONS[0]);
  const [skuVolume, setSkuVolume] = useState('ate_500');
  const [acceptedLgpd, setAcceptedLgpd] = useState(true);

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  const isEnterpriseErp =
    erpSystem.includes('Senior') || erpSystem.includes('Totvs') || erpSystem.includes('SAP');

  const formatPhone = (val: string) => {
    const digits = val.replace(/\D/g, '').slice(0, 11);
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhone(e.target.value));
  };

  const buildWhatsappFallbackUrl = () => {
    const text = encodeURIComponent(
      `Olá! Gostaria de auditar o catálogo da minha empresa no R-MAX.\n\n` +
      `*Nome:* ${name || 'Não informado'}\n` +
      `*WhatsApp:* ${phone}\n` +
      `*ERP / Sistema:* ${erpSystem}\n` +
      `*Volume de SKUs:* ${skuVolume.replace('_', ' ')}\n` +
      `*Origem:* Calculadora R-MAX (${originTab})`
    );
    // WhatsApp comercial oficial da parceria 4Shift & R-MAX
    return `https://api.whatsapp.com/send/?phone=5511972269833&text=${text}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    const rawDigits = phone.replace(/\D/g, '');
    if (rawDigits.length < 10) {
      setErrorMessage('Por favor, informe um WhatsApp válido com DDD.');
      return;
    }

    if (!acceptedLgpd) {
      setErrorMessage('É necessário concordar com a política de privacidade e LGPD.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch('/api/leads/calculator', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          lead_source: 'catalog_audit',
          name,
          phone,
          erp_system: erpSystem,
          sku_volume: skuVolume,
          origin_tab: originTab,
          metadata: {
            isEnterpriseErp,
            submittedAt: new Date().toISOString(),
          },
        }),
      });

      if (!response.ok) {
        throw new Error('Falha na comunicação com o servidor.');
      }

      setIsSuccess(true);
    } catch (err: any) {
      console.warn('Falha no envio direto do lead, disponibilizando fallback ativo via WhatsApp:', err);
      setErrorMessage(
        'Não foi possível registrar automaticamente pelo navegador. Clique no botão abaixo para concluir o contato diretamente via WhatsApp Comercial.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/70 backdrop-blur-xs animate-in fade-in duration-200">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden text-slate-800">
        
        {/* Cabeçalho */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/80">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center font-black text-sm">
              <Database className="w-4 h-4 text-emerald-700" />
            </div>
            <div>
              <h3 className="text-base font-black text-slate-900">
                Auditar Catálogo Completo
              </h3>
              <p className="text-xs text-slate-500">
                Análise em lote de margens, custos e Reforma Tributária
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="p-6">
          {isSuccess ? (
            <div className="text-center py-4 space-y-4">
              <div className="w-14 h-14 mx-auto rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                <CheckCircle2 className="w-8 h-8" />
              </div>

              <div>
                <h4 className="text-lg font-black text-slate-900">
                  Solicitação Registrada com Sucesso
                </h4>
                <p className="text-xs text-slate-600 mt-1 max-w-sm mx-auto">
                  {isEnterpriseErp ? (
                    <>
                      Identificamos que sua empresa utiliza <strong>{erpSystem}</strong>. Nossa equipe de engenharia fiscal entrará em contato via WhatsApp para apresentar o conector passivo (DBLink seguro, sem custo de ambiente de desenvolvimento).
                    </>
                  ) : (
                    <>
                      Seus dados foram validados. Entraremos em contato com as instruções para o envio do lote de produtos via planilha CSV.
                    </>
                  )}
                </p>
              </div>

              {isEnterpriseErp ? (
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-left text-xs space-y-2 text-slate-700">
                  <div className="flex items-center gap-1.5 font-bold text-slate-900">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    <span>Garantia de Segurança Corporativa</span>
                  </div>
                  <p className="text-[11px] text-slate-500 leading-relaxed">
                    Operação 100% passiva (somente leitura). O conector não executa comandos de gravação ou alteração na sua base de dados do ERP.
                  </p>
                </div>
              ) : (
                <div className="p-4 bg-emerald-50/60 rounded-xl border border-emerald-200 text-left text-xs space-y-1.5 text-emerald-950">
                  <div className="flex items-center gap-1.5 font-bold">
                    <FileSpreadsheet className="w-4 h-4 text-emerald-700" />
                    <span>Modelo de Planilha Padrão</span>
                  </div>
                  <p className="text-[11px] text-emerald-800 leading-relaxed">
                    Você poderá auditar custos de compra, mark-up e incidência de CBS/IBS item a item sem digitação manual.
                  </p>
                </div>
              )}

              <div className="pt-2">
                <button
                  onClick={onClose}
                  className="w-full py-2.5 px-4 bg-slate-900 hover:bg-slate-800 text-white font-bold rounded-xl text-xs transition-colors"
                >
                  Concluir e Voltar à Calculadora
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="text-xs text-slate-600 leading-relaxed bg-slate-50 p-3 rounded-xl border border-slate-200">
                Audite centenas ou milhares de itens de uma só vez para identificar produtos que dão prejuízo invisível e antecipar o impacto da CBS e IBS.
              </div>

              {/* Campo 1: Nome */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  1. Nome do Responsável / Decisor *
                </label>
                <input
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ex: Carlos Silva"
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>

              {/* Campo 2: WhatsApp */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  2. WhatsApp Corporativo com DDD *
                </label>
                <input
                  type="tel"
                  required
                  value={phone}
                  onChange={handlePhoneChange}
                  placeholder="(11) 99999-9999"
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>

              {/* Campo 3: ERP em Uso */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  3. Qual ERP ou Sistema sua empresa utiliza? *
                </label>
                <select
                  value={erpSystem}
                  onChange={(e) => setErpSystem(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                >
                  {ERP_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              {/* Campo 4: Volume de SKUs (Chips M3) */}
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  4. Volume Aproximado de Produtos no Catálogo *
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {SKU_OPTIONS.map((sku) => {
                    const isSelected = skuVolume === sku.id;
                    return (
                      <button
                        key={sku.id}
                        type="button"
                        onClick={() => setSkuVolume(sku.id)}
                        className={`py-2 px-2.5 text-center rounded-xl text-[11px] font-bold border transition-all ${
                          isSelected
                            ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                            : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                        }`}
                      >
                        {sku.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Checkbox LGPD */}
              <div className="pt-1">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={acceptedLgpd}
                    onChange={(e) => setAcceptedLgpd(e.target.checked)}
                    className="mt-0.5 w-4 h-4 text-emerald-600 rounded border-slate-300 focus:ring-emerald-500"
                  />
                  <span className="text-[11px] text-slate-500 leading-tight">
                    Concordo em compartilhar esses dados corporativos para receber o diagnóstico de catálogo e autorizo o contato técnico da equipe R-MAX.
                  </span>
                </label>
              </div>

              {/* Erro ou Fallback Ativo */}
              {errorMessage && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-900 space-y-2">
                  <div className="flex items-start gap-1.5">
                    <AlertCircle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
                    <span>{errorMessage}</span>
                  </div>
                  <a
                    href={buildWhatsappFallbackUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-1.5 w-full py-2 px-3 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-lg text-xs transition-colors"
                  >
                    <span>Concluir pelo WhatsApp Comercial</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </a>
                </div>
              )}

              {/* Botão de Envio */}
              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-300 text-white font-bold rounded-xl text-xs shadow-md shadow-emerald-600/20 transition-all hover:scale-[1.01] active:scale-[0.99]"
                >
                  {isLoading ? (
                    <span>Registrando solicitação...</span>
                  ) : (
                    <>
                      <span>Solicitar Diagnóstico de Catálogo</span>
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>

      </div>
    </div>
  );
};
